import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import { Eye, EyeOff, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="forgot-page">
      <div className="w-full max-w-md space-y-6 bg-white border-2 border-asap-deep p-8 shadow-hard">
        <Logo />
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-asap-orange font-bold">Password Help</div>
          <h1 className="font-display text-3xl uppercase text-asap-deep mt-1">Forgot Password</h1>
          <p className="mt-2 text-sm text-slate-600">Enter your account email. If we find it, we'll text you a one-time reset link.</p>
        </div>

        {sent ? (
          <div className="p-4 border-2 border-emerald-500 bg-emerald-50 text-emerald-800 flex items-start gap-3" data-testid="forgot-sent">
            <CheckCircle2 size={22} className="shrink-0"/>
            <div>
              <div className="font-bold">Reset request received.</div>
              <div className="text-sm mt-1">If <span className="font-mono">{email}</span> exists, we've sent a reset link to the phone on that account. Check your text messages — the link is valid for 1 hour.</div>
              <div className="text-sm mt-3 border-t border-emerald-300 pt-3">
                <strong>Didn't get a text within 5 minutes?</strong> Contact dispatch and we'll reset it for you:
                <div className="mt-2 flex flex-wrap gap-2">
                  <a href="tel:+18317477141" data-testid="forgot-call" className="inline-flex items-center gap-1 px-3 py-1.5 border-2 border-emerald-600 text-emerald-800 font-bold text-xs uppercase tracking-wider hover:bg-emerald-600 hover:text-white">Call 831-747-7141</a>
                  <a href="sms:+17653301951" data-testid="forgot-text" className="inline-flex items-center gap-1 px-3 py-1.5 border-2 border-emerald-600 text-emerald-800 font-bold text-xs uppercase tracking-wider hover:bg-emerald-600 hover:text-white">Text 765-330-1951</a>
                  <a href="mailto:asaproadrunners@rs-asap.com?subject=Password%20reset%20help" data-testid="forgot-email-link" className="inline-flex items-center gap-1 px-3 py-1.5 border-2 border-emerald-600 text-emerald-800 font-bold text-xs uppercase tracking-wider hover:bg-emerald-600 hover:text-white">Email us</a>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <label className="block">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-asap-deep mb-1">Email</div>
              <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 border-2 border-asap-deep focus:border-asap-orange focus:outline-none" placeholder="you@example.com"/>
            </label>
            {err && <div className="text-asap-red text-sm font-bold border-2 border-asap-red bg-red-50 px-3 py-2" data-testid="forgot-error">{err}</div>}
            <button type="submit" disabled={busy} data-testid="forgot-submit" className="w-full px-6 py-3 bg-asap-yellow text-asap-deep font-display uppercase tracking-wide border-2 border-asap-deep shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2">
              <KeyRound size={16}/> {busy ? "Sending…" : "Send Reset Link"}
            </button>
          </form>
        )}

        <div className="text-sm border-t pt-4">
          <Link to="/login" data-testid="forgot-back" className="inline-flex items-center gap-1 text-asap-deep font-bold hover:text-asap-orange"><ArrowLeft size={14}/> Back to login</Link>
        </div>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { setErr("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { setErr("Passwords don't match"); return; }
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="reset-page">
      <div className="w-full max-w-md space-y-6 bg-white border-2 border-asap-deep p-8 shadow-hard">
        <Logo />
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-asap-orange font-bold">Reset Password</div>
          <h1 className="font-display text-3xl uppercase text-asap-deep mt-1">Choose a new password</h1>
        </div>

        {!token && (
          <div className="p-4 border-2 border-asap-red bg-red-50 text-asap-red text-sm">Missing reset token. Use the link from your text message.</div>
        )}

        {done ? (
          <div className="p-4 border-2 border-emerald-500 bg-emerald-50 text-emerald-800 flex items-start gap-3" data-testid="reset-done">
            <CheckCircle2 size={22} className="shrink-0"/>
            <div>
              <div className="font-bold">Password updated.</div>
              <div className="text-sm mt-1">Redirecting you to log in…</div>
            </div>
          </div>
        ) : token && (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <PwField label="New Password" value={pw} onChange={setPw} show={show} setShow={setShow} tid="reset-pw"/>
            <PwField label="Confirm Password" value={pw2} onChange={setPw2} show={show} setShow={setShow} tid="reset-pw2"/>
            {err && <div className="text-asap-red text-sm font-bold border-2 border-asap-red bg-red-50 px-3 py-2" data-testid="reset-error">{err}</div>}
            <button type="submit" disabled={busy} data-testid="reset-submit" className="w-full px-6 py-3 bg-asap-yellow text-asap-deep font-display uppercase tracking-wide border-2 border-asap-deep shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2">
              <KeyRound size={16}/> {busy ? "Updating…" : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function PwField({ label, value, onChange, show, setShow, tid }) {
  return (
    <label className="block">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-asap-deep mb-1">{label}</div>
      <div className="relative">
        <input data-testid={tid} type={show ? "text" : "password"} required minLength={6} value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3 pr-12 border-2 border-asap-deep focus:border-asap-orange focus:outline-none" placeholder="••••••••"/>
        <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-asap-deep hover:text-asap-orange" aria-label={show ? "Hide" : "Show"}>
          {show ? <EyeOff size={18}/> : <Eye size={18}/>}
        </button>
      </div>
    </label>
  );
}
